import api from './api';

/**
 * Client for the on-demand invoice reconciler ("Reconcile Invoice" button).
 *
 * `preview` is a DRY-RUN: it returns what the reconciler WOULD write for an
 * order's derived lines (chassis management fee / fuel surcharge) alongside the
 * lines on the invoice today — the two sides of the reconcile modal. Writing is
 * gated server-side (SAVERULES_SAVE_MODE='native' + the reconcile-apply
 * permission); in cmsapi/shadow the user reviews the staged (blue) values and
 * saves them through the normal invoicing Save.
 */

export interface ReconcileOp {
  action: 'upsert' | 'remove' | 'extra_in_rate' | string;
  item: string; // CHASSIS MANAGEMENT | FUEL SURCHARGE | dray item (extra_in_rate)
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
  /** null => the reconciler would INSERT a new line; set => update/remove that recno */
  existingRecno: number | null;
  reason: string;
}

export interface ReconcileCurrentLine {
  recno: number;
  item: string;
  qty: number;
  rate: number;
  rateType: string;
  total: number;
  description: string;
}

export interface ReconcileAddlCost {
  action: 'insert' | 'update' | string;
  qty: number;
  rate: number;
  total: number;
  payStatus: string;
  existingAcRecno: number | null;
  reason: string;
}

export interface ReconcilePlan {
  pronumber: number;
  invoiceRecno: number | null;
  dryRun: boolean;
  /** set => nothing to do (template / already invoiced) */
  skipped: string | null;
  numChassisDays: number;
  hasChanges: boolean;
  /** SAVERULES_SAVE_MODE: cmsapi | shadow | native */
  saveMode: string;
  /** writes allowed (native mode); else preview-only */
  canApply: boolean;
  /** this call actually wrote (apply mode) */
  applied: boolean;
  /** derived lines on the invoice now (legacy side of the side-by-side) */
  currentLines: ReconcileCurrentLine[];
  /** the reconciler's proposed changes (right side) */
  ops: ReconcileOp[];
  addlCost: ReconcileAddlCost | null;
}

/** An invoice line as it stands on the form now — sent so the reconciler diffs
 *  against the user's unsaved edits (e.g. a cleared FSC), not the saved DB rows. */
export interface ReconcileCurrentLineIn {
  recno: number;
  item: string;
  qty: number;
  rate: number;
  rateType: string;
  total: number;
  description: string;
}

export const invoiceReconcileService = {
  /** Dry-run preview — the reconciler's proposed derived lines vs the current invoice.
   *  Pass the form's current lines so unsaved edits are reflected in the diff. */
  async preview(
    pronumber: number,
    currentLines?: ReconcileCurrentLineIn[],
  ): Promise<ReconcilePlan> {
    const res = await api.post(
      `/orders/${pronumber}/reconcile-invoice?dryRun=true`,
      currentLines ? { currentLines } : {},
    );
    return res.data;
  },
};

/* ── Invoice Estimate ───────────────────────────────────────────────────────
 * The reconciler returns a PLAN (a list of changes). The ESTIMATE returns the
 * projected invoice — every line already there plus every charge the order has
 * earned — which is what a person reviewing an order actually wants to see.
 *
 * It also covers the charges the reconciler alone can't: hazmat, bobtail, chassis
 * split, waiting time, pre-pull and yard storage now come from the same server-side
 * engine as chassis and fuel, instead of a separate client-side rule table.
 */

export interface LineItemOverride {
  recno: number;
  liitem: string;
  lineItemRecno: number;
  userid: string;
  overrideDateTime: string | null;
  reason: string;
  /** Additional Comments — the specifics behind the list reason. '' when none. */
  comments: string;
  /** What the decision was worth, captured when it was made — the NET, matching the
   *  Invoice Overrides report so one decision never reads as two different figures.
   *  Carried on the override itself rather than derived from the estimate, because the
   *  estimate refuses to run on an invoiced order and so cannot price anything there.
   *  null = never captured, which is NOT the same as 0. */
  amount: number | null;
  grossAmount: number | null;
  billedAmount: number | null;
}

/** Where a projected line came from. */
export type EstimateSource =
  /** on the invoice; the engine has no opinion about it (a manual charge, no quote counterpart) */
  | 'existing'
  /** on the invoice, checked against the quote/derivation, and correct */
  | 'matched'
  | 'update'
  | 'add'
  | 'remove'
  /** an override is stopping the engine touching this charge */
  | 'held';

export interface EstimatedLine {
  lineNumber: number;
  item: string;
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
  source: EstimateSource;
  /** null => this line isn't on the invoice yet */
  existingRecno: number | null;
  /** figures projected from an assumed date (undated chassis window / missing
   *  fuelcalcdate) rather than derived from the order's own dates */
  estimated: boolean;
  /** which assumption was made, e.g. "chassis end date assumed 06/10/2026" */
  estimateBasis: string;
  /** false => stage the line but NOT the rate. An assumed rate is shown so the user
   *  can see the charge accruing; it is never filled in on their behalf. */
  applyRate: boolean;
  reason: string;
  /** The record `reason` names — a `dataTables.registry` key plus a recno — so the
   *  label can open it. Sent as the registry key, not a URL: the frontend owns where
   *  its own pages live. Empty when the reason points at nothing. */
  reasonLinkTable: string;
  reasonLinkRecno: number;
  /** Something that bears on the decision but is NOT a proposed change: why the
   *  engine has no opinion on this line, or what else is true of the charge.
   *  Separate from `reason` because a line can carry both — and a line with no
   *  proposal at all can only carry this one. */
  note: string;
  /** what the invoice bills today, when this line proposes changing it — null on an add */
  currentRate: number | null;
  currentQty: number | null;
  currentTotal: number | null;
  /** the rate type the CURRENT rate is in — a proposal can change the type, and the old
   *  figure has to be rendered in its own (a superseded 68.7% is not $68.70) */
  currentRateType: string | null;
  /** on an OVERRIDDEN line: what would have been charged had the override not stopped
   *  it — the revenue deliberately declined */
  withheldQty: number | null;
  withheldRate: number | null;
  withheldTotal: number | null;
  /** The override protecting this line was agreed at a figure that has since moved, so
   *  nobody has decided about the charge as it now stands. Refuses Pending Approval ->
   *  RESOLVED until it is re-verified. Always false on the accumulating charges (fuel,
   *  chassis management, yard storage, waiting time), which are overridden before their
   *  figure settles by design. */
  overrideNeedsReview: boolean;
  /** The invoice bills this and the engine found NO reason for it — the rule that owns
   *  the item never fired. Sits ALONGSIDE a `matched` source rather than replacing it:
   *  the rate does agree with the quote, which is worth keeping, but it says nothing
   *  about whether the charge belongs. Shown, never acted on — nothing is proposed
   *  against it and approval is not refused. */
  rateMatchedUnevidenced: boolean;
  /** The rate that prices this line has not been published yet — today, only the weekly
   *  fuel index, which lands the Tuesday or Wednesday after the week it covers. Shown as
   *  estimated AND refuses approval: nothing the order does settles it, and the
   *  alternative is invoicing a confident $0. `estimateBasis` says which reading is
   *  missing and how far the published ones reach. */
  awaitingPublishedRate: boolean;
  /** What the override was AGREED at — `withheldTotal` is the figure today. Both are
   *  sent so a reviewer sees what moved, not only that something did. */
  overrideStampedTotal: number | null;
  /** quoteadditionalcharges.recno the rate came from — operational charge rules only.
   *  null for chassis / fuel, which are derived and have no quote-charge row. */
  sourceChargeRecno: number | null;
  /** which invoice on the order carries this line. An order's charges don't all land on
   *  one document — the accessorial-billing customers take their chassis fee on a
   *  separate accessorial. null => that accessorial doesn't exist yet. */
  invoiceRecno?: number | null;
  /** 0 = the main invoice */
  accessorialNum?: number;
}

/**
 * Does this line belong to the MAIN invoice?
 *
 * The one predicate that separates what the pro's Invoicing form can act on from what it
 * merely displays. The form holds the main invoice's rows and nothing else, so an
 * accessorial line has no row to update and no business being appended to a document
 * billed to a different party.
 *
 * `accessorialNum` is optional on the wire, and absent means MAIN — before the projection
 * covered accessorials every line was a main-invoice line, so that is what an untagged
 * payload is describing.
 */
export const isMainInvoiceLine = (
  line: Pick<EstimatedLine, 'accessorialNum'>,
): boolean => (line.accessorialNum ?? 0) === 0;

/** One invoice on the order, as it would stand once the plan is applied. Totals only —
 *  the lines live in the flat `lines` roll-up, each tagged with its `invoiceRecno`. */
export interface InvoiceProjection {
  invoiceRecno: number | null;
  accessorialNum: number;
  isMain: boolean;
  /** true => applying would CREATE this accessorial; it has no lines today */
  isNew: boolean;
  /** the party this document bills. An accessorial routinely bills someone OTHER than
   *  the order's billto — the consignee, the steamship line, even the chassis provider —
   *  so naming the document alone doesn't tell the reader whose invoice they're reading.
   *  0 / '' on a pending accessorial, which doesn't exist yet and has no billto. */
  billto: number;
  billtoName: string;
  /** The v1 quote this document prices off — its own, else the main invoice's. An
   *  accessorial on a different quote is judged separately: its overrides are its own,
   *  and a decision taken against the main invoice does not reach it. 0 = no linkage. */
  quoteNumber: number;
  currentTotal: number;
  projectedTotal: number;
  hasChanges: boolean;
  /** This document is BILLED. An order's invoices are billed one at a time, so this is
   *  per document: its lines are the reconciliation frozen when it was invoiced
   *  (`archivedAt`), or — billed in V1 or before archiving began, `archivedAt` null —
   *  its lines as they stand. Nothing on it can be staged or overridden; the order's
   *  open documents carry on as usual. */
  invoiced?: boolean;
  archivedAt?: string | null;
}

export interface InvoiceEstimate {
  pronumber: number;
  invoiceRecno: number | null;
  /** set => nothing to project (template / already invoiced) */
  skipped: string | null;
  numChassisDays: number;
  hasChanges: boolean;
  hasEstimates: boolean;
  currentTotal: number;
  projectedTotal: number;
  lines: EstimatedLine[];
  /** charges rejected outright — an override with no invoice line. Each carries what it
   *  would have been worth. */
  rejected: EstimatedLine[];
  /** everything the overrides on this pro declined to bill, on and off the invoice */
  withheldTotal: number;
  /** per-invoice breakdown; `lines` / `currentTotal` / `projectedTotal` are the roll-up
   *  across these */
  invoices?: InvoiceProjection[];
  saveMode: string;
  canApply: boolean;
  /** true => served from the published cache rather than computed now. The difference
   *  that matters to a user: a cached estimate can't see unsaved edits on the form. */
  fromCache?: boolean;
  /** the date the projections were made FROM (YYYY-MM-DD). An undated chassis window and
   *  a missing fuel index are projected from this, so an estimate is stale the day after. */
  estimateAsOf?: string | null;
  /** when the cache row was written */
  syncedAt?: string | null;
  /** true => EVERY invoice on the order is billed, so the whole estimate is frozen and
   *  never acted on. An order with an open invoice is not archived even when some of its
   *  documents are — those say so on their own `invoices[].invoiced`. */
  archived?: boolean;
  /** when the latest of those snapshots was taken */
  archivedAt?: string | null;
  /** Why no CHASSIS MANAGEMENT fee is proposed, as a short label — present whether or
   *  not the invoice carries a chassis line, which is the case with nothing else to
   *  explain it. Absent when the QUOTE is the source of the reason (billed IN RATE, a
   *  $0.00 rate): the Quote Charges row already shows that, and the server filters it
   *  so both endpoints agree. */
  chassisSkipReason?: string | null;
  /** `dataTables.registry` key for the record the reason names (a Custom Chassis
   *  Charges rule), never a URL — the frontend owns where its pages live. */
  chassisSkipLinkTable?: string | null;
  chassisSkipLinkRecno?: number;
}

/** A form row sent to the estimate. `recno` is optional — a row the user just added
 *  has none yet, and the estimate must see it or it keeps suggesting a charge that's
 *  already on screen. */
export interface EstimateLineIn {
  recno?: number;
  item: string;
  qty: number;
  rate: number;
  rateType: string;
  total: number;
  description: string;
  glCode?: string;
}

export const invoiceEstimateService = {
  /** The projected invoice for one pro, computed NOW. Pass the form's current rows so
   *  the projection reflects unsaved edits. READ-ONLY. */
  async estimate(
    pronumber: number,
    currentLines?: EstimateLineIn[],
  ): Promise<InvoiceEstimate> {
    const res = await api.post(
      `/orders/${pronumber}/invoice-estimate`,
      currentLines ? { currentLines } : {},
    );
    return res.data;
  },

  /** The estimate the sweep last PUBLISHED for this pro, or null if it holds none.
   *
   *  Null is a real answer, not a failure: the sweep may not have covered this pro yet,
   *  or it may have been billed since the last run. Either way the caller computes live
   *  rather than rendering zeros, which would read as "this order owes nothing". */
  async cached(pronumber: number): Promise<InvoiceEstimate | null> {
    try {
      const res = await api.get(`/orders/${pronumber}/invoice-estimate`);
      return res.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },
};

/* ── Line-item overrides ────────────────────────────────────────────────────
 * A user's veto over the reconciler: "don't touch this charge, here's why."
 *
 * Keyed by line-item TYPE, not by line — so it holds even when the charge isn't on
 * the invoice at all ("we are not billing chassis on this pro"). The line must have
 * an item; its RATE may be blank.
 *
 * SCOPE: honored by 360 only for now. Production still saves through cmsapi.pl, so V1
 * keeps recomputing an overridden chassis/fuel line on the next save. The override
 * becomes authoritative at the native cutover.
 */



export const invoiceOverrideService = {
  /** Active overrides on the order's main invoice. */
  async list(pronumber: number): Promise<LineItemOverride[]> {
    const res = await api.get(`/orders/${pronumber}/invoice-overrides`);
    return res.data;
  },

  /** Record an override. `reason` is mandatory server-side as well as here; `comments`
   *  is too when the reason is flagged Require Comments in Data Management. */
  async create(
    pronumber: number,
    input: {
      liitem: string; reason: string; comments?: string; lineItemRecno?: number;
      /** Which DOCUMENT the decision is taken on. Not provenance — it resolves the
       *  quote the override binds to. Omit for the main invoice. */
      invoiceRecno?: number;
    },
  ): Promise<LineItemOverride> {
    const res = await api.post(`/orders/${pronumber}/invoice-overrides`, input);
    return res.data;
  },

  /** Reword one in place. The decision is unchanged, so the original author and date
   *  stay put and the history row carries both the old and new comment. */
  async update(
    pronumber: number, recno: number, reason: string, comments = '',
  ): Promise<LineItemOverride> {
    const res = await api.patch(
      `/orders/${pronumber}/invoice-overrides/${recno}`, { reason, comments });
    return res.data;
  },

  /** Re-agree one at what the charge is worth NOW.
   *
   *  The way out of review. An override is agreed at a figure; when the charge moves it
   *  stops counting as accounted for and refuses Pending Approval -> RESOLVED. This
   *  confirms the arrangement still stands against the new amount and rewrites the
   *  stamp. Deliberately separate from `update`, which rewords a decision and leaves its
   *  figure alone — fixing a typo must not silently re-agree a number nobody looked at. */
  async reverify(pronumber: number, recno: number): Promise<LineItemOverride> {
    const res = await api.post(
      `/orders/${pronumber}/invoice-overrides/${recno}/reverify`);
    return res.data;
  },

  /** Revoke one. Soft-delete server-side — the decision stays in the audit trail. */
  async revoke(pronumber: number, recno: number): Promise<void> {
    await api.delete(`/orders/${pronumber}/invoice-overrides/${recno}`);
  },
};

export default invoiceReconcileService;
